import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Palette, Plus, Trash2, RotateCcw, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/sales-settings/palette`;
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });
const slug = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const L = {
  'pt-BR': { title: 'Paleta de cores da Simulação IA', hint: 'A IA gera uma imagem por cor desta lista (externas para fachadas, internas para ambientes).', exterior: 'Cores externas', interior: 'Cores internas', add: 'Adicionar cor', name: 'Nome', desc: 'Descrição p/ IA (opcional)', save: 'Salvar paleta', reset: 'Restaurar padrão RbA', saved: 'Paleta salva', resetDone: 'Paleta restaurada', confirmReset: 'Restaurar a paleta padrão Renewal by Andersen?' },
  en: { title: 'AI Simulation color palette', hint: 'AI generates one image per color in this list (exterior for facades, interior for rooms).', exterior: 'Exterior colors', interior: 'Interior colors', add: 'Add color', name: 'Name', desc: 'AI description (optional)', save: 'Save palette', reset: 'Restore RbA defaults', saved: 'Palette saved', resetDone: 'Palette restored', confirmReset: 'Restore the default Renewal by Andersen palette?' },
};

const Group = ({ title, colors, onChange, t, prefix }) => {
  const upd = (i, k, v) => onChange(colors.map((c, idx) => (idx === i ? { ...c, [k]: v, ...(k === 'name' ? { id: slug(v) || c.id } : {}) } : c)));
  return (
    <div className="space-y-2" data-testid={`${prefix}-group`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title} ({colors.length})</p>
      {colors.map((c, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`${prefix}-row-${i}`}>
          <input type="color" value={c.hex} onChange={(e) => upd(i, 'hex', e.target.value.toUpperCase())} className="h-9 w-10 rounded border border-slate-300 p-0.5 bg-white cursor-pointer" data-testid={`${prefix}-hex-${i}`} />
          <Input value={c.name} onChange={(e) => upd(i, 'name', e.target.value)} placeholder={t.name} className="h-9 w-36" data-testid={`${prefix}-name-${i}`} />
          <Input value={c.hex} onChange={(e) => upd(i, 'hex', e.target.value)} className="h-9 w-24 font-mono text-xs" maxLength={7} />
          <Input value={c.desc || ''} onChange={(e) => upd(i, 'desc', e.target.value)} placeholder={t.desc} className="h-9 flex-1 text-xs hidden sm:block" />
          <button type="button" onClick={() => onChange(colors.filter((_, idx) => idx !== i))} className="h-9 w-9 rounded-md text-red-600 hover:bg-red-50 flex items-center justify-center" data-testid={`${prefix}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="text-xs" onClick={() => onChange([...colors, { id: `cor_${Date.now()}`, name: '', hex: '#888888', desc: '' }])} data-testid={`${prefix}-add`}><Plus className="h-3.5 w-3.5 mr-1" /> {t.add}</Button>
    </div>
  );
};

const SalesPaletteEditor = ({ language = 'pt-BR' }) => {
  const t = L[language] || L['pt-BR'];
  const [pal, setPal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(API, auth()).then((r) => setPal({ exterior: r.data.exterior, interior: r.data.interior })).catch((e) => toast.error(e.response?.data?.detail || 'Erro'));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await axios.put(API, pal, auth()); setPal(data); toast.success(t.saved); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm(t.confirmReset)) return;
    try { const { data } = await axios.delete(API, auth()); setPal({ exterior: data.exterior, interior: data.interior }); toast.success(t.resetDone); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  if (!pal) return null;
  return (
    <div className="space-y-4 p-4 rounded-lg border bg-white" data-testid="sales-palette-editor">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-slate-800 text-white flex items-center justify-center shrink-0"><Palette className="h-4 w-4" /></div>
        <div><p className="font-semibold text-sm">{t.title}</p><p className="text-xs text-slate-600">{t.hint}</p></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Group title={t.exterior} colors={pal.exterior} onChange={(c) => setPal((p) => ({ ...p, exterior: c }))} t={t} prefix="palette-ext" />
        <Group title={t.interior} colors={pal.interior} onChange={(c) => setPal((p) => ({ ...p, interior: c }))} t={t} prefix="palette-int" />
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={reset} data-testid="palette-reset-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" /> {t.reset}</Button>
        <Button type="button" size="sm" onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="palette-save-btn"><Save className="h-3.5 w-3.5 mr-1" /> {t.save}</Button>
      </div>
    </div>
  );
};

export default SalesPaletteEditor;
